import React, { useMemo, useState } from 'react'

import DOMPurify from 'dompurify'
import {
  SurveyAnsweringForm,
  type SurveyAnsweringFormMode,
  type SurveyAnswers,
  type SurveyFormBuilderElement,
  type SurveyFormSubmitResult,
  type SurveySubmitAnswers,
} from '@factorialco/f0-react'
import { ModuleId } from '@factorialco/f0-react/dist/experimental'
import {
  FormFeaturesDocument,
  type FormFeaturesQuery,
  type FormsFormStatusEnum,
} from 'generated/resources/graphql'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import { formStatusMapper } from 'modules/forms/lib/formStatusMapper'

import { FromQuestionnaireToForm } from 'modules/forms/components/newFormEditor/helpers'
import { useAnswerForm } from 'modules/forms/hooks/useAnswerForm'
import { useLoadForm } from 'modules/forms/hooks/useForms'
import { Form } from 'modules/forms/lib/Form'
import {
  applySurveySubmitAnswersToForm,
  mapFormToQuestionnaire,
} from 'modules/forms/lib/surveyAnsweringFormHelpers'
import { type FormResourceType } from 'modules/forms/lib/types'

export type { FormResourceType } from 'modules/forms/lib/types'

function hasFeature(data: FormFeaturesQuery | undefined, featureName: string) {
  if (!data) {
    return false
  }

  return (
    data?.features.featuresConnection.nodes.some(
      (feature) => feature.name === featureName && feature.enabled
    ) ?? false
  )
}

const FORM_STATUS_MAP: Record<string, FormsFormStatusEnum> = {
  active: 'active',
  archived: 'archived',
  draft: 'draft',
  finished: 'finished',
  scheduled: 'scheduled',
}

function toFormsFormStatus(status: string): FormsFormStatusEnum {
  return FORM_STATUS_MAP[status] ?? 'draft'
}

function useFormModule(resourceType: FormResourceType) {
  const { navigation } = useNavigation()

  return useMemo(() => {
    const map: Record<FormResourceType, { id: ModuleId; label: string; href: string }> = {
      'Surveys::Survey': {
        id: 'engagement',
        label: i18n.t('sidebar.engagement'),
        href: navigation.engagement.fullPath(),
      },
      'Trainings::Training': {
        id: 'company_trainings',
        label: i18n.t('sidebar.trainings'),
        href: navigation.trainings.fullPath(),
      },
    }

    return map[resourceType]
  }, [navigation, resourceType])
}

type FormRenderProps = {
  mode: 'preview' | 'answer'
  resourceType: FormResourceType
  isOpen: boolean
  membershipId?: string
  onClose: () => void
  onSubmitSuccess?: () => void
  defaultValues?: Partial<SurveyAnswers>
} & ({ form: Form } | { formId: string })

const FormRender: React.FC<FormRenderProps> = ({
  resourceType,
  isOpen,
  onClose,
  onSubmitSuccess,
  membershipId,
  mode,
  defaultValues,
  ...props
}) => {
  let form = 'form' in props ? props.form : undefined
  const formId = 'formId' in props ? props.formId : undefined

  const module = useFormModule(resourceType)
  const {
    company,
    access: { locale: sessionLocale },
  } = useCurrentSession()
  const [locale] = useState(sessionLocale)

  const { data: featuresData } = useQuery(FormFeaturesDocument, {
    variables: { companyId: company.id },
    fetchPolicy: 'cache-first',
  })

  const hasTranslationsEnabled = hasFeature(featuresData, 'forms_translations')

  const { form: loadedForm, loadingForm } = useLoadForm({
    formId,
    resourceType,
    locale: hasTranslationsEnabled ? locale : undefined,
  })
  form ||= loadedForm

  const elements = useMemo<SurveyFormBuilderElement[]>(() => {
    if (!form) {
      return []
    }

    return FromQuestionnaireToForm(mapFormToQuestionnaire(form).elements)
  }, [form])

  const resolvedRenderingMode: SurveyAnsweringFormMode =
    mode === 'answer' ? 'stepped' : 'all-questions'

  const { saveAnswers } = useAnswerForm()

  const handleSubmit = async (answers: SurveySubmitAnswers): Promise<SurveyFormSubmitResult> => {
    if (mode === 'preview') {
      return {
        success: false,
        errors: {
          form: i18n.t('forms.render.submit_disabled_preview'),
        },
      }
    }

    if (!form || !membershipId) {
      return {
        success: false,
        errors: {
          form: i18n.t('common.error'),
        },
      }
    }

    applySurveySubmitAnswersToForm(form, answers)

    const saved = await saveAnswers({
      form,
      membershipId,
      onSuccess: () => {
        app.setFlash({
          type: 'correct',
          message: i18n.t('forms.render.answers_saved'),
        })

        form?.resetFormAnswers()
        onClose()
        onSubmitSuccess?.()
      },
    })

    if (!saved) {
      return {
        success: false,
      }
    }

    return {
      success: true,
    }
  }

  const hasDefaultValues = !!defaultValues
  const position = hasDefaultValues ? ('right' as const) : ('fullscreen' as const)

  const allowToChangeFullscreen = position === 'right'

  const resourceHeader = useMemo(() => {
    if (!form || mode !== 'preview') {
      return undefined
    }

    const { label: statusLabel, status: statusVariant } = formStatusMapper(
      toFormsFormStatus(form.status)
    )

    return {
      metadata: [
        {
          label: i18n.t('forms.visibility'),
          value: {
            type: 'text' as const,
            content: form.anonymous ? i18n.t('forms.anonymous') : i18n.t('forms.visible'),
          },
        },
      ],
      status: {
        label: statusLabel,
        text: statusLabel,
        variant: statusVariant,
      },
    }
  }, [form, mode])

  return (
    <SurveyAnsweringForm
      elements={elements}
      mode={resolvedRenderingMode}
      title={form?.title ?? ''}
      description={
        form?.description ? DOMPurify.sanitize(form.description, { ALLOWED_TAGS: [] }) : undefined
      }
      module={module}
      isOpen={isOpen}
      onClose={onClose}
      allowToChangeFullscreen={allowToChangeFullscreen}
      errorTriggerMode="on-submit"
      loading={loadingForm}
      defaultValues={defaultValues}
      position={position}
      resourceHeader={resourceHeader}
      {...(mode === 'preview'
        ? {
            preview: true,
          }
        : {
            onSubmit: handleSubmit,
          })}
      labels={{
        empty: {
          title: i18n.t('f0.survey_answering_form.labels.empty.title'),
          description: i18n.t('f0.survey_answering_form.labels.empty.description'),
          emoji: '📝',
        },
      }}
    />
  )
}

export default FormRender
